import { identity, startCase, uniq } from 'es-toolkit';
import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import { createSelector } from 'reselect';

import { INDETERMINATE, PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import { Member } from '@paragon/contract-library';
import { PropertiesPanel, recognizedBraceTypes, recognizedMemberTypes } from '@paragon/layout-models';
import { hasFreeSubscription, UpgradeNotification } from '@paragon/login-tools';
import { naturalSortByProperty } from '@paragon/utilities';

import { RootState } from '../../../../../store';
import { setMemberIsFieldInstalled, setMemberTypes } from '../../../../../store/actions/editMemberActions';
import { showUpgradeAccountDialog } from '../../../../../store/actions/popupActions';
import { getActiveComponentDesign } from '../../../../../store/selectors/getActiveComponentDesignSelector';
import { getAsyncRequestForActiveComponentDesign } from '../../../../../store/selectors/getAsyncRequestForActiveComponentDesignSelector';
import { getSelectedItemEntries } from '../../../../../store/selectors/getSelectedItemSelector';
import { propertiesShouldBeViewOnly } from '../../../../../store/selectors/viewOnlyModeSelectors';
import { setMemberName } from '../../../../../store/slices/componentDesignsSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { PropertiesPanelMenuButton } from '../PropertiesPanelMenuButton';
import { PropertiesPanelSubtitle } from '../PropertiesPanelSubtitle';
import { LumberPropertySubPanel } from './LumberPropertySubPanel';
import { MemberCutsSubPanel } from './MemberCutsSubPanel';
import { MemberGeometrySubPanel } from './MemberGeometrySubPanel';
import { SingleMemberAdvancedProperties } from './SingleMemberAdvancedProperties';

const getSelectedMembers = createSelector(getSelectedItemEntries, (selectedEntries) =>
  selectedEntries
    .filter((entry) => entry.type === 'member' || entry.type === 'brace')
    .map((entry) => entry.item as Member),
);

function isMemberUpdatePendingSelector(state: RootState) {
  const asyncRequest = getAsyncRequestForActiveComponentDesign(state);
  return !!asyncRequest && asyncRequest.type !== 'updateFeatures';
}
export function MemberProperties() {
  const dispatch = useAppDispatch();

  const componentDesign = useAppSelector(getActiveComponentDesign);

  const members = useAppSelector(getSelectedMembers);
  const singleMember = members.length === 1 ? members[0] : null;

  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);

  const isFreeUser = useAppSelector(hasFreeSubscription);
  const isMemberUpdatePending = useAppSelector(isMemberUpdatePendingSelector);

  const [showAdvanced, setShowAdvanced] = useState(false);

  const singleMemberIsBrace = singleMember?.relativeGeometry?.isBrace ?? false;

  const allAreNonBraces = members.every((m) => !(m.relativeGeometry?.isBrace ?? false));
  const memberTypeValues = uniq(members.map((m) => m.memberType));
  const memberTypeValue = memberTypeValues.length === 1 ? memberTypeValues[0] : INDETERMINATE;

  const modifyPanelView = () => {
    showAdvanced ? setShowAdvanced(false) : setShowAdvanced(true);
  };

  const disabled = isViewOnly || isMemberUpdatePending;

  const title = singleMemberIsBrace
    ? singleMember!.memberType === 'Scab'
      ? 'Scab Properties'
      : 'Brace Properties'
    : singleMember
      ? 'Member Properties'
      : `Member Properties (${members.length} selected)`;

  return (
    <PropertiesPanel title={title} titleButton={<PropertiesPanelMenuButton type="member" guid={members[0].guid} />}>
      <UpgradeNotification
        sx={{ marginLeft: 1, marginTop: 1, marginBottom: 2 }}
        description="Upgrade to unlock all features."
        trialDescription="Start your free trial to unlock all features."
        onUpgradeClick={() => dispatch(showUpgradeAccountDialog())}
      />

      {singleMember && (
        <>
          <PropertiesPanelSubtitle
            endButton={
              <Button size="small" variant="text" onClick={modifyPanelView}>
                {showAdvanced ? `Hide Advanced` : `Show Advanced`}
              </Button>
            }
          >
            Info
          </PropertiesPanelSubtitle>

          <Box>
            <Box sx={{ m: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="default"
                    checked={singleMember!.isFieldInstalled}
                    onChange={(_, checked) => {
                      dispatch(setMemberIsFieldInstalled(componentDesign!.guid, singleMember, checked));
                    }}
                  />
                }
                label="Is Field Installed"
                disabled={disabled}
              />
            </Box>

            <PropertySelectField
              sx={{ m: 1 }}
              property={singleMemberIsBrace ? 'Brace Type' : 'Member Type'}
              value={singleMember.memberType}
              options={naturalSortByProperty(
                singleMemberIsBrace ? recognizedBraceTypes : recognizedMemberTypes,
                identity,
              )}
              getName={startCase}
              submit={(newType) => {
                if (isFreeUser) {
                  dispatch(
                    showUpgradeAccountDialog(
                      'Editing member types is not available with the free plan. You can upgrade your account to access this feature.',
                    ),
                  );
                } else {
                  dispatch(setMemberTypes(componentDesign!.guid, members, newType));
                }
              }}
              disabled={disabled || singleMemberIsBrace || isFreeUser}
            />

            <PropertyTextInput
              sx={{ m: 1 }}
              fullWidth
              property="Name"
              value={singleMember.name}
              disabled={disabled}
              submit={(name) => {
                // Normally we would use setMember to automatically set the insertion or component design member as appropriate.
                // When we change the name, however, we also need to change the reference line names, which are contained outside
                // the member. So in this case, we manually dispatch different actions rather than sequential, multiple actions
                // to update the member and reference lines separately. A single action seems less likely to possibly leave the
                // truss in a broken state.
                dispatch(setMemberName({ componentDesignGuid: componentDesign!.guid, member: singleMember, name }));
              }}
            />

            {showAdvanced && <SingleMemberAdvancedProperties />}
          </Box>

          <MemberGeometrySubPanel member={singleMember} disabled={disabled} showAdvanced={showAdvanced} />
        </>
      )}

      {!singleMember && allAreNonBraces && (
        <PropertySelectField
          sx={{ m: 1 }}
          property="Member Type"
          value={memberTypeValue}
          options={naturalSortByProperty(recognizedMemberTypes, identity)}
          getName={startCase}
          submit={(newType) => {
            if (isFreeUser) {
              dispatch(
                showUpgradeAccountDialog(
                  'Editing member types is not available with the free plan. You can upgrade your account to access this feature.',
                ),
              );
            } else {
              dispatch(setMemberTypes(componentDesign!.guid, members, newType));
            }
          }}
          disabled={disabled || isFreeUser}
        />
      )}

      <MemberCutsSubPanel members={members} disabled={disabled} />

      <PropertiesPanelSubtitle>Lumber</PropertiesPanelSubtitle>
      <LumberPropertySubPanel showAdvanced={showAdvanced} members={members} />
    </PropertiesPanel>
  );
}
